import React, { createContext, useContext, useEffect, useState } from 'react';

type Language = 'ar' | 'en';

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
  direction: 'ltr' | 'rtl';
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation dictionary
const translations = {
  ar: {
    // Navigation
    'nav.why_flyn': 'لماذا FLYN؟',
    'nav.features': 'الخصائص',
    'nav.how_it_works': 'كيف تعمل؟',
    'nav.partnerships': 'الشراكات',
    'nav.faq': 'الأسئلة الشائعة',
    'nav.login': 'تسجيل الدخول',
    'nav.register': 'إنشاء حساب الشركة',
    
    // Hero Section
    'hero.title': 'حوِّل أرقام شركتك إلى قرارات مالية ذكية',
    'hero.subtitle': 'منصة FLYN Corporate تُقدِّم لك لوحة قيادة موحدة تُحلل إنفاقك، تقارن أداء أقسامك، وتفعل خصومات الشراكات تلقائيًا – مدعومة بالذكاء الاصطناعي، وتمويل فوري، وامتثال لمنصة مدد.',
    'hero.cta_primary': 'ابدأ استخدام المنصة الآن مجانًا',
    'hero.cta_secondary': 'شاهد كيف تعمل المنصة',
    
    // Why FLYN Section
    'why.title': 'لماذا FLYN؟',
    'why.ai_intelligence': 'ذكاء اصطناعي حقيقي',
    'why.ai_description': 'توصيات فورية لتحسين الإنفاق وتوزيع الميزانيات بذكاء.',
    'why.smart_cards': 'بطاقات تحليلية ذكية',
    'why.smart_cards_description': 'كل قسم في شركتك يتحول إلى بطاقة تعرض الأداء والفرص والمخاطر.',
    'why.banking_connection': 'ربط بنكي مباشر',
    'why.banking_description': 'تتبع لحظي لحساباتك مع جميع البنوك المعتمدة في السعودية عبر Open Banking.',
    'why.mudad_support': 'دعم كامل لـ مدد',
    'why.mudad_description': 'متابعة الرواتب واكتشاف التأخيرات آليًا – مع تقارير التزام جاهزة.',
    'why.instant_financing': 'تمويل فوري',
    'why.financing_description': 'منصتك تقيمك ائتمانيًا وتتيح لك طلب تمويل في 6 ساعات فقط.',
    'why.partnerships': 'شراكات وخصومات',
    'why.partnerships_description': 'وفر آلاف الريالات من خلال خصومات تلقائية عند التعامل مع شركائنا.',
    
    // Auth
    'auth.login_title': 'تسجيل الدخول',
    'auth.register_title': 'إنشاء حساب مؤسسة جديد',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.company_name': 'اسم الشركة',
    'auth.commercial_register': 'السجل التجاري',
    'auth.city': 'المدينة',
    'auth.phone': 'رقم الجوال',
    'auth.login_button': 'تسجيل الدخول',
    'auth.register_button': 'إنشاء الحساب',
    'auth.forgot_password': 'نسيت كلمة المرور؟',
    'auth.have_account': 'لديك حساب؟',
    'auth.no_account': 'ليس لديك حساب؟',
    'auth.create_account': 'إنشاء حساب جديد',
    
    // Footer
    'footer.newsletter_success': 'تم الاشتراك بنجاح! سنرسل لك أحدث الأخبار والعروض.',
    'footer.newsletter_error': 'حدث خطأ أثناء الاشتراك. يرجى المحاولة مرة أخرى.',
    
    // Dashboard translations
    'dashboard.overview': 'نظرة عامة',
    'dashboard.analytics': 'التحليلات',
    'dashboard.cards': 'البطاقات الذكية',
    'dashboard.partnerships': 'الشراكات',
    'dashboard.financing': 'التمويل',
    'dashboard.settings': 'الإعدادات',
    'dashboard.profile': 'الملف الشخصي',
    'dashboard.account': 'إعدادات الحساب',
    'dashboard.logout': 'تسجيل الخروج',
    'dashboard.search': 'البحث...',
    'dashboard.total_balance': 'إجمالي الرصيد',
    'dashboard.monthly_revenue': 'الإيرادات الشهرية',
    'dashboard.monthly_expenses': 'المصروفات الشهرية',
    'dashboard.active_cards': 'البطاقات النشطة',
    'dashboard.partnerships_savings': 'وفورات الشراكات',
    'dashboard.financing_available': 'التمويل المتاح',
    'dashboard.compared_last_month': 'مقارنة بالشهر الماضي',
    'dashboard.revenue_growth': 'نمو الإيرادات',
    'dashboard.expense_reduction': 'تقليل المصروفات',
    'dashboard.smart_cards_active': 'بطاقات ذكية نشطة',
    'dashboard.partnership_benefits': 'فوائد الشراكات',
    'dashboard.instant_financing': 'تمويل فوري',
    'dashboard.financial_overview': 'النظرة المالية الشاملة',
    'dashboard.ai_insights': 'الرؤى الذكية',
    'dashboard.quick_trends': 'الاتجاهات السريعة',
    'dashboard.smart_cards': 'البطاقات الذكية',
    'dashboard.quick_actions': 'الإجراءات السريعة',
    'dashboard.welcome': 'مرحباً بك في',
    'dashboard.company_name': 'شركة الرياض للتجارة',
    'dashboard.connected_updated': 'متصل ومحدث',
    'dashboard.last_sync': 'آخر مزامنة',
    'dashboard.connected_banks': 'البنوك المتصلة',
    'dashboard.banks_connected': 'بنوك متصلة',
    'dashboard.compliance': 'الامتثال',
    'dashboard.hr_mudad': 'مُدد - حماية الأجور',
    'dashboard.mudad_status': 'حالة مُدد',
    'dashboard.mudad_connected': 'متصل ومحدث',
    'dashboard.mudad_employees': 'موظف مسجل',
    'dashboard.mudad_compliance': 'معدل الامتثال',
    'dashboard.mudad_alerts': 'تنبيهات مُدد',
    'dashboard.zatca_status': 'حالة زاتكا',
    'dashboard.zatca_invoices': 'فاتورة إلكترونية',
  },
  en: {
    // Navigation
    'nav.why_flyn': 'Why FLYN?',
    'nav.features': 'Features',
    'nav.how_it_works': 'How It Works?',
    'nav.partnerships': 'Partnerships',
    'nav.faq': 'FAQ',
    'nav.login': 'Login',
    'nav.register': 'Create Company Account',
    
    // Hero Section
    'hero.title': 'Transform Your Company Numbers into Smart Financial Decisions',
    'hero.subtitle': 'FLYN Corporate platform provides you with a unified dashboard that analyzes your spending, compares department performance, and automatically activates partnership discounts – powered by AI, instant financing, and Mudad compliance.',
    'hero.cta_primary': 'Start Using Platform Now Free',
    'hero.cta_secondary': 'See How Platform Works',
    
    // Why FLYN Section
    'why.title': 'Why FLYN?',
    'why.ai_intelligence': 'Real AI Intelligence',
    'why.ai_description': 'Instant recommendations for spending optimization and smart budget distribution.',
    'why.smart_cards': 'Smart Analytical Cards',
    'why.smart_cards_description': 'Each department in your company becomes a card showing performance, opportunities, and risks.',
    'why.banking_connection': 'Direct Banking Connection',
    'why.banking_description': 'Real-time tracking of your accounts with all approved banks in Saudi Arabia via Open Banking.',
    'why.mudad_support': 'Full Mudad Support',
    'why.mudad_description': 'Payroll monitoring and automatic delay detection – with ready compliance reports.',
    'why.instant_financing': 'Instant Financing',
    'why.financing_description': 'Your platform assesses you creditwise and allows you to request financing in just 6 hours.',
    'why.partnerships': 'Partnerships & Discounts',
    'why.partnerships_description': 'Save thousands of riyals through automatic discounts when dealing with our partners.',
    
    // Auth
    'auth.login_title': 'Login',
    'auth.register_title': 'Create New Company Account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.company_name': 'Company Name',
    'auth.commercial_register': 'Commercial Register',
    'auth.city': 'City',
    'auth.phone': 'Phone Number',
    'auth.login_button': 'Login',
    'auth.register_button': 'Create Account',
    'auth.forgot_password': 'Forgot Password?',
    'auth.have_account': 'Have an account?',
    'auth.no_account': 'Don\'t have an account?',
    'auth.create_account': 'Create New Account',
    
    // Dashboard translations
    'dashboard.overview': 'Overview',
    'dashboard.analytics': 'Analytics',
    'dashboard.cards': 'Smart Cards',
    'dashboard.partnerships': 'Partnerships',
    'dashboard.financing': 'Financing',
    'dashboard.settings': 'Settings',
    'dashboard.profile': 'Profile',
    'dashboard.account': 'Account Settings',
    'dashboard.logout': 'Logout',
    'dashboard.search': 'Search...',
    'dashboard.total_balance': 'Total Balance',
    'dashboard.monthly_revenue': 'Monthly Revenue',
    'dashboard.monthly_expenses': 'Monthly Expenses',
    'dashboard.active_cards': 'Active Cards',
    'dashboard.partnerships_savings': 'Partnership Savings',
    'dashboard.financing_available': 'Available Financing',
    'dashboard.compared_last_month': 'Compared to last month',
    'dashboard.revenue_growth': 'Revenue Growth',
    'dashboard.expense_reduction': 'Expense Reduction',
    'dashboard.smart_cards_active': 'Smart Cards Active',
    'dashboard.partnership_benefits': 'Partnership Benefits',
    'dashboard.instant_financing': 'Instant Financing',
    'dashboard.financial_overview': 'Financial Overview',
    'dashboard.ai_insights': 'AI Insights',
    'dashboard.quick_trends': 'Quick Trends',
    'dashboard.smart_cards': 'Smart Cards',
    'dashboard.quick_actions': 'Quick Actions',
    'dashboard.welcome': 'Welcome to',
    'dashboard.company_name': 'Riyadh Trading Company',
    'dashboard.connected_updated': 'Connected & Updated',
    'dashboard.last_sync': 'Last Sync',
    'dashboard.connected_banks': 'Connected Banks',
    'dashboard.banks_connected': 'Banks Connected',
    'dashboard.compliance': 'Compliance',
    'dashboard.hr_mudad': 'Mudad - Wage Protection',
    'dashboard.mudad_status': 'Mudad Status',
    'dashboard.mudad_connected': 'Connected & Updated',
    'dashboard.mudad_employees': 'Registered Employee',
    'dashboard.mudad_compliance': 'Compliance Rate',
    'dashboard.mudad_alerts': 'Mudad Alerts',
    'dashboard.zatca_status': 'ZATCA Status',
    'dashboard.zatca_invoices': 'Electronic Invoice',
  }
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language') as Language;
    return savedLanguage || 'ar'; // Default to Arabic
  });

  useEffect(() => {
    const html = document.documentElement;
    
    if (language === 'ar') {
      html.setAttribute('dir', 'rtl');
      html.setAttribute('lang', 'ar');
    } else {
      html.setAttribute('dir', 'ltr');
      html.setAttribute('lang', 'en');
    }
    
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguageState(prevLang => prevLang === 'ar' ? 'en' : 'ar');
  };

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations[typeof language]] || key;
  };

  const value = {
    language,
    toggleLanguage,
    setLanguage,
    t,
    direction: language === 'ar' ? 'rtl' as const : 'ltr' as const,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
